export interface TemplateField {
  key: string;
  label: string;
  guidance: string;
  placeholder: string;
  rows?: number;
}

export interface FrameworkTemplate {
  id: "technical" | "past-performance" | "approach";
  name: string;
  description: string;
  sections: TemplateField[];
}

export const TEMPLATES: FrameworkTemplate[] = [
  {
    id: "technical",
    name: "Technical Response",
    description: "Full technical proposal narrative",
    sections: [
      { key: "exec_summary", label: "Executive Summary", guidance: "3–5 sentences. Win theme + value proposition + why us.", placeholder: "CatalystAgile is uniquely positioned to…", rows: 5 },
      { key: "understanding", label: "Understanding of Requirements", guidance: "Mirror the buyer's language. Show you read the RFP — name their pain points.", placeholder: "We understand that [agency] is seeking…", rows: 6 },
      { key: "solution", label: "Proposed Solution", guidance: "Concrete components, architecture, and how each maps to a requirement.", placeholder: "Our solution comprises three layers…", rows: 8 },
      { key: "implementation", label: "Implementation Plan", guidance: "Phases, milestones, dependencies. Include weeks, not vague terms.", placeholder: "Phase 1 (Weeks 1–4): Discovery…", rows: 6 },
      { key: "team", label: "Team & Qualifications", guidance: "Named key personnel with relevant certs. Avoid generic bios.", placeholder: "Program Manager: [Name], PMP, 12 yrs…", rows: 5 },
      { key: "pricing", label: "Pricing Approach", guidance: "Pricing model + assumptions + what is/isn't included.", placeholder: "Fixed-fee structure totaling $X…", rows: 5 },
    ],
  },
  {
    id: "past-performance",
    name: "Past Performance",
    description: "Single past-performance case study",
    sections: [
      { key: "overview",   label: "Project Overview",   guidance: "Client, contract value, period of performance, your role.", placeholder: "Client: Dept. of X | $4.2M | 2022–2024…", rows: 4 },
      { key: "challenge",  label: "Challenge",          guidance: "The buyer's problem in their words. Quantify the pain.", placeholder: "The agency faced 6-week onboarding…", rows: 5 },
      { key: "solution",   label: "Solution Delivered", guidance: "What you actually built/did. Specific, not generic.", placeholder: "We deployed a unified intake portal…", rows: 6 },
      { key: "results",    label: "Measurable Results", guidance: "Hard numbers. % improved, $ saved, time cut.", placeholder: "Reduced cycle time by 62% (6w → 2.3w)…", rows: 5 },
      { key: "relevance",  label: "Relevance to This RFP", guidance: "Explicit mapping: 'This is relevant because…'", placeholder: "Directly mirrors Section 3.2 of this RFP…", rows: 4 },
    ],
  },
  {
    id: "approach",
    name: "Approach & Methodology",
    description: "Methodology and program controls",
    sections: [
      { key: "methodology",   label: "Methodology",        guidance: "Name it. PMI, Agile, hybrid — and why it fits this engagement.", placeholder: "We will apply a hybrid Agile-Waterfall…", rows: 6 },
      { key: "risk",          label: "Risk Mitigation",    guidance: "Top 3–5 risks, likelihood × impact, mitigation owner.", placeholder: "Risk 1: Data-migration scope creep — Mitigation:…", rows: 6 },
      { key: "qa",            label: "Quality Assurance",  guidance: "Standards (ISO, CMMI), reviews, acceptance criteria.", placeholder: "QA gates at the end of each sprint…", rows: 5 },
      { key: "communication", label: "Communication Plan", guidance: "Cadence, channels, escalation path, named stakeholders.", placeholder: "Weekly steering committee, biweekly demos…", rows: 5 },
    ],
  },
];

export function templateToMarkdown(t: FrameworkTemplate, values: Record<string, string>): string {
  const lines = [`# ${t.name}`, ""];
  for (const s of t.sections) {
    lines.push(`## ${s.label}`, "");
    lines.push((values[s.key] ?? "").trim() || `_[${s.label} — to be completed]_`);
    lines.push("");
  }
  return lines.join("\n");
}
